import { createRoot } from "react-dom/client";
import { MaxUI } from "@maxhub/max-ui";
import "@maxhub/max-ui/dist/styles.css";
import "./index.css";
import { AppRoutes } from "./routes/AppRoutes";
import { useEffect } from "react";
import { LoadingProvider } from "./shared/context/LoadingContext";
import { ErrorBoundary } from "./shared/components/ErrorBoundary";
import {
  detectDeepLinkToken,
  storePendingToken,
} from "./shared/services/deep-link.service";

const App = () => {
  useEffect(() => {
    // --------------------------------------------------------------
    // Test mode initialization
    // When --mode test is active (npm run dev:test), VITE_TEST_MODE
    // is loaded from .env.test and exposed via import.meta.env.
    // Bootstrap the test environment so the app can run without a
    // backend — sets up localStorage tokens, mock window.WebApp data,
    // and pre-populates Zustand stores.
    // --------------------------------------------------------------

    console.log("MiniApp version 0.0.7");

    try {
      const env = import.meta.env.VITE_TEST_MODE as string | undefined;
      if (env === "true" || env === "1") {
        console.log("[APP] Test mode detected — bootstrapping...");
        import("./test-mode/bootstrap")
          .then(({ bootstrapTestMode }) => {
            bootstrapTestMode();
          })
          .catch((err) => {
            console.error("[APP] Failed to bootstrap test mode:", err);
          });
      }
    } catch (e) {
      // import.meta.env may not be available in all environments
      console.log("[APP] Could not check test mode:", e);
    }

    // Debug logging for Max Messenger initData.
    // Полный raw initData выводится в консоль, чтобы его можно было скопировать
    // в VITE_INIT_DATA (.env.dev) для авторизации в dev-режиме (браузер вне webview).
    if (window.WebApp) {
      console.log("Max MiniApp detected");
      console.log("Platform:", window.WebApp.platform);
      console.log("Version:", window.WebApp.version);

      if (window.WebApp.initData) {
        console.log("INIT_DATA ->", window.WebApp.initData);
      }
      if (window.WebApp.initDataUnsafe) {
        console.log("User data:", window.WebApp.initDataUnsafe.user);
        console.log("Start param:", window.WebApp.initDataUnsafe.start_param);
        // Hash is required by LoginPage for the auto-login call to AuthService
        console.log(
          "InitData hash exists:",
          !!window.WebApp.initDataUnsafe.hash,
        );
      }
    }

    // --------------------------------------------------------------
    // Deep Link detection
    // Пытаемся извлечь qrToken из start_param (Max Messenger deep-link)
    // или из URL query string (?qrToken=...). Если токен найден,
    // сохраняем его в sessionStorage для использования после авторизации.
    // --------------------------------------------------------------
    const deepLinkToken = detectDeepLinkToken();
    if (deepLinkToken) {
      console.log(
        "[APP] Deep link token detected — saving for post-login auto-scan:",
        deepLinkToken,
      );
      storePendingToken(deepLinkToken);
    } else {
      console.log("[APP] No deep link token detected");
    }
  }, []);

  return (
    <LoadingProvider>
      <AppRoutes />
    </LoadingProvider>
  );
};

// Wrap in MaxUI to apply JetBrains Mono font and MaxUI theming
const Root = () => (
  <MaxUI>
    <App />
  </MaxUI>
);

// Render the app to the DOM, wrapped in ErrorBoundary
// to prevent white screens on unhandled render errors.
const rootElement = document.getElementById("root");
if (rootElement) {
  createRoot(rootElement).render(
    <ErrorBoundary>
      <Root />
    </ErrorBoundary>,
  );
}

export default App;
